<template>
  <div
    :class="$b({
      [`theme-${themeOverride}`]: themeOverride !== 'disabled',
      linked: activeItemHasLinkedContent || activeItemHasExternalLink
    })"
    :style="promoStylesOverride"
    @click="handleClick"
  >
    <ul
      :class="$b('list')"
      class="global-wrapper"
    >
      <li
        v-for="(bannerItem, bannerIndex) in bannerItems"
        :key="bannerItem.id"
        :class="
          $b('list-item', {
            active: activeIndex === bannerIndex,
            out:
              bannerItems.length > 1 &&
              (activeIndex === bannerIndex + 1 ||
                (bannerIndex + 1 === bannerItems.length && activeIndex === 0)),
            hasMobileText:
              bannerItem.mobileLine1 != '' || bannerItem.mobileLine2 != '',
          })
        "
      >
        <!-- eslint-disable vue/no-v-html -->
        <span
          :class="
            $b('text', {
              desktop: true,
            })
          "
          v-html="bannerItem.text"
        />
        <span
          v-if="bannerItem.mobileLine1"
          :class="
            $b('text', {
              mobile: true,
            })
          "
          v-html="bannerItem.mobileLine1"
        />
        <br
          v-if="bannerItem.mobileLine1 && bannerItem.mobileLine2"
          :class="$b('mobile-break')"
        />
        <span
          v-if="bannerItem.mobileLine2"
          :class="
            $b('text', {
              mobile: true,
            })
          "
          v-html="bannerItem.mobileLine2"
        />
        <!-- eslint-enable -->

        <div :class="$b('icon')">
          <VisionIcon
            v-if="!!bannerItem.linkedContent
              || !!bannerItem.externalLink
              || bannerItem.link?.internalLink
            "
            icon="chevron"
            :colour="iconColour"
            size="xx-small"
            :rotate="!!bannerItem.linkedContent ? 90 : 0"
          />
        </div>
      </li>
    </ul>
  </div>
</template>

<script>
import { storeToRefs } from 'pinia'
const { buildInternalLink } = useUrlBuilder()

export default {
  name: 'StripBanner',

  props: {
    bannerItems: {
      type: Array,
      default: () => [],
      required: true,
    },
    themeOverride: {
      type: String,
      default: 'disabled'
    }
  },

  setup () {
    const modalStore = useModalStore()
    const sidebarStore = useSidebarStore()
    const { pageTheme } = storeToRefs(usePageStore())
    const { styles: promoStyles } = storeToRefs(usePromoStore())
    let promoStylesOverride = promoStyles
    if (!promoStylesOverride.value) {
      promoStylesOverride = `
        --promo-bg-tint: rgba(28, 37, 42, var(--promo-bg-tint-alpha-override, 1));
        --promo-bg: rgba(17, 17, 17, var(--promo-bg-alpha-override, 1));
        --promo-mid-bg: rgba(23, 71, 95, var(--promo-mid-bg-alpha-override, 1));
        --promo-key: rgba(77, 161, 202, var(--promo-key-alpha-override, 1));
        --promo-highlight: rgba(149, 198, 221, var(--promo-highlight-alpha-override, 1));
        --promo-shine: rgba(255, 255, 255, var(--promo-shine-alpha-override, 1))
      `
    }

    const openLinkedConent = (content) => {
      if (content._modelApiKey === 'dialog') {
        modalStore.OPEN_MODAL_PAGE({
          slug: content.slug
        })
      } else if (content._modelApiKey === 'sidebar_page') {
        sidebarStore.setActiveSidebar(content.slug)
      }
    }

    return { openLinkedConent, pageTheme, promoStylesOverride }
  },

  data: () => ({
    stripBannerInterval: null,
    activeIndex: 0,
  }),

  computed: {
    activeItemHasLinkedContent() {
      return !!this.bannerItems[this.activeIndex].linkedContent
        || !!this.bannerItems[this.activeIndex].link?.internalLink
    },
    activeItemHasExternalLink() {
      return !!this.bannerItems[this.activeIndex].externalLink
    },
    iconColour() {
      if (this.pageTheme === 'ln4') return 'black'

      return 'white'
    }
  },

  watch: {
    bannerItems(newValue, oldValue) {
      if (newValue.length > 1 && oldValue.length === 1) {
        this.setupTickerInterval()
      }
    },
  },

  mounted() {
    this.setupTickerInterval()
  },

  beforeUnmount() {
    if (this.bannerItems.length > 1) {
      clearInterval(this.stripBannerInterval)
    }
  },

  methods: {
    setupTickerInterval() {
      if (this.bannerItems.length <= 1) return

      if (this.stripBannerInterval) {
        clearInterval(this.stripBannerInterval)
      }

      this.stripBannerInterval = setInterval(() => {
        if (this.activeIndex >= this.bannerItems.length - 1) {
          this.activeIndex = 0
        } else {
          this.activeIndex += 1
        }
      }, 5000)
    },
    async handleClick () {
      await this.$track.global.globalBannerClicked({
        bannerId: this.bannerItems[this.activeIndex].id,
        bannerText: this.bannerItems[this.activeIndex].text,
        position: this.activeIndex + 1
      })

      if(this.activeItemHasLinkedContent) {
        if (this.bannerItems[this.activeIndex].link?.internalLink) {
          await navigateTo(buildInternalLink(
            this.bannerItems[this.activeIndex].link
          ))
        } else {
          this.openLinkedConent(
            this.bannerItems[this.activeIndex].linkedContent
          )
        }
      } else if (this.activeItemHasExternalLink) {
        await navigateTo(this.bannerItems[this.activeIndex].externalLink, {
          external: true
        })
      }
    }
  },
}
</script>

<style lang="postcss">
.StripBanner {
  @apply flex px-4 w-full overflow-hidden justify-center relative z-[12];

  &--linked {
    @apply cursor-pointer;
    -webkit-tap-highlight-color: transparent;
  }
}

.StripBanner__list {
  @apply flex shrink-0 justify-center relative h-full w-full;
}

.StripBanner__list-item {
  @apply absolute top-1/2 left-1/2 translate-x-[-50%]
    opacity-0
    transition-[opacity,transform]
    ease-in-out
    duration-500
    w-full
    text-center
    text-[13px];

  &--active {
    @apply opacity-100 translate-y-[-50%];
  }

  &--out {
    @apply opacity-0 translate-y-[-100%];
  }

  &--has-mobile-text {
    .StripBanner__text {
      &--mobile {
        @apply inline-block md:hidden;
      }

      &--desktop {
        @apply hidden md:inline-block;
      }
    }
  }
}

.StripBanner__icon {
  @apply inline-flex
    transform translate-y-[1px] ml-xxs;
}

.StripBanner__mobile-break {
  @apply block md:hidden;
}
</style>
